/**
 * MedicalRecordsCard — every medical record on file for one lead in one place:
 * inbound faxes that were matched to them AND files a recruiter uploaded straight
 * onto the profile. Click a row to view it (presigned, PHI-audited, opens in a
 * new tab). "Upload record" drops a PDF or photo onto the lead; it's stored like
 * an inbound fax and read by the nightly AI profile builder, which posts any
 * medications/diagnoses/labs it finds onto this same Profile tab (unconfirmed,
 * one-tap Verify).
 *
 * Each row can be renamed (the pencil) so a recruiter can name a record
 * something relevant; inbound faxes arrive pre-named by the AI from their first
 * pages ("Cardiology visit note — Duke Health"), which a recruiter can override.
 * The rename editor also exposes a permanent Delete (type-to-confirm).
 *
 * "Select" enters a merge mode: pick two or more records and combine them into
 * one PDF (POST /contacts/:id/documents/merge) — the originals are removed by
 * default. The combined file is stored like an upload, so the nightly AI reads it.
 *
 * Reads GET /contacts/:id/documents (all doc types) and shows only the
 * 'medical_record' ones. Refetches on [contactId, reloadToken, pingTick] so a
 * freshly-matched (and AI-named) fax appears without a manual reload.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { FileText, Loader2, Upload, ExternalLink, Pencil, Check, X, Send, Trash2, Layers, CheckSquare, Square } from 'lucide-react';
import api from '../../../services/api';
import { ContactDocument } from '../../types';
import { relTime } from '../../lib';
import { useConfirm } from '../ui';
import DeleteRecordModal from './DeleteRecordModal';
import MergeRecordsModal from './MergeRecordsModal';

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

const MAX_BYTES = 50 * 1024 * 1024;
// Only the formats the nightly AI OCR can actually read (PDF + PNG/JPEG via
// Textract), so every accepted upload really does get reviewed. Excluding HEIC
// here also makes iOS Safari transcode a selected iPhone photo to JPEG for us.
const ACCEPT = '.pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg';

interface Props {
  contactId: string;
  reloadToken?: number;
  /** Debounced Realtime ping from ContactDetail — silent refetch (matched fax). */
  pingTick?: number;
  /** Rendered inside ProfileTab's frameless grouped list, which owns the
   *  divide-y chrome: drop this card's own border/background/shadow. Absent =
   *  the standalone card, byte for byte what renders today. */
  frameless?: boolean;
}

function sourceBadge(source: string): { label: string; cls: string } {
  switch (source) {
    case 'fax_inbound':
      return { label: 'Faxed in', cls: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' };
    case 'upload':
      return { label: 'Uploaded', cls: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300' };
    case 'merged':
      return { label: 'Merged', cls: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' };
    default:
      return { label: source, cls: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' };
  }
}

function fmtSize(n: number | null): string {
  if (!n || n <= 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export default function MedicalRecordsCard({ contactId, reloadToken = 0, pingTick = 0, frameless = false }: Props) {
  const confirm = useConfirm();
  const [docs, setDocs] = useState<ContactDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [savingId, setSavingId] = useState<string | null>(null);
  const [sendingId, setSendingId] = useState<string | null>(null);
  const [sentIds, setSentIds] = useState<Set<string>>(new Set());
  // Merge (select) mode + permanent delete.
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [mergeOpen, setMergeOpen] = useState(false);
  const [merging, setMerging] = useState(false);
  const [deleteDoc, setDeleteDoc] = useState<ContactDocument | null>(null);
  const [deleting, setDeleting] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const editRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async (initial = false) => {
    if (initial) setLoading(true);
    try {
      const res = await api.get(`/api/crm/contacts/${contactId}/documents`);
      const all: ContactDocument[] = res.data?.documents || [];
      setDocs(all.filter((d) => d.doc_type === 'medical_record'));
      setError(null);
    } catch (e: any) {
      if (initial) setError(errMsg(e));
    } finally {
      if (initial) setLoading(false);
    }
  }, [contactId]);

  useEffect(() => { load(true); }, [load]);
  useEffect(() => { if (reloadToken || pingTick) load(false); }, [reloadToken, pingTick, load]);

  const view = async (documentId: string) => {
    setActionError(null);
    try {
      const res = await api.get(`/api/crm/documents/${documentId}/url`);
      if (res.data?.url) window.open(res.data.url, '_blank', 'noopener');
      else setActionError('That document is unavailable.');
    } catch (e: any) {
      setActionError(errMsg(e));
    }
  };

  const onFile = async (f: File | null) => {
    if (fileRef.current) fileRef.current.value = '';
    if (!f || uploading) return;
    setActionError(null);
    setNotice(null);
    if (f.size > MAX_BYTES) { setActionError('That file is larger than 50MB.'); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', f);
      const res = await api.post(`/api/crm/contacts/${contactId}/documents`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const doc: ContactDocument | undefined = res.data?.document;
      if (doc) setDocs((prev) => [doc, ...prev]);
      else await load(false);
      setNotice("Uploaded. The AI reviews it tonight and adds any diagnoses or medications it finds to this profile.");
    } catch (e: any) {
      setActionError(errMsg(e));
    } finally {
      setUploading(false);
    }
  };

  const startEdit = (d: ContactDocument) => {
    setActionError(null);
    setEditingId(d.id);
    setEditValue(d.filename);
    setTimeout(() => editRef.current?.focus(), 0);
  };
  const cancelEdit = () => { setEditingId(null); setEditValue(''); };
  const saveEdit = async (d: ContactDocument) => {
    const name = editValue.trim();
    if (!name || name === d.filename) { cancelEdit(); return; }
    setSavingId(d.id);
    setActionError(null);
    try {
      const res = await api.patch(`/api/crm/documents/${d.id}`, { filename: name });
      const updated: ContactDocument | undefined = res.data?.document;
      setDocs((prev) => prev.map((x) => (x.id === d.id ? (updated || { ...x, filename: name }) : x)));
      cancelEdit();
    } catch (e: any) {
      setActionError(errMsg(e));
    } finally {
      setSavingId(null);
    }
  };

  const sendToCrio = async (d: ContactDocument) => {
    if (sendingId) return;
    if (!(await confirm({
      title: `Send “${d.filename}” to CRIO’s document inbox?`,
      confirmLabel: 'Send',
    }))) return;
    setActionError(null);
    setNotice(null);
    setSendingId(d.id);
    try {
      const res = await api.post(`/api/crm/documents/${d.id}/send-to-crio`);
      setSentIds((prev) => new Set(prev).add(d.id));
      setNotice(
        res.data?.overridden
          ? 'Sent to CRIO (test override — routed to the override inbox).'
          : 'Sent to CRIO. A coordinator files it under the patient’s subject folder.'
      );
    } catch (e: any) {
      setActionError(errMsg(e));
    } finally {
      setSendingId(null);
    }
  };

  // ---- Merge (select) mode ----
  const enterSelectMode = () => {
    cancelEdit();
    setActionError(null);
    setNotice(null);
    setSelectedIds(new Set());
    setSelectMode(true);
  };
  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedIds(new Set());
  };
  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Selected records in DISPLAY order (top-to-bottom) — the order they combine in.
  const selectedDocs = docs.filter((d) => selectedIds.has(d.id));
  const selectedSize = selectedDocs.reduce((n, d) => n + (d.file_size || 0), 0);

  const doMerge = async (name: string, deleteOriginals: boolean) => {
    if (merging) return;
    setMerging(true);
    setActionError(null);
    setNotice(null);
    try {
      const documentIds = selectedDocs.map((d) => d.id);
      const res = await api.post(`/api/crm/contacts/${contactId}/documents/merge`, {
        documentIds, filename: name, deleteOriginals,
      });
      const newDoc: ContactDocument | undefined = res.data?.document;
      const deleted: string[] = res.data?.deleted || [];
      setDocs((prev) => {
        const remaining = prev.filter((d) => !deleted.includes(d.id));
        return newDoc ? [newDoc, ...remaining] : remaining;
      });
      setMergeOpen(false);
      exitSelectMode();
      setNotice(
        deleteOriginals
          ? 'Records combined into one. The AI reviews the combined file tonight.'
          : 'Records combined into one. The originals were kept.'
      );
    } catch (e: any) {
      setActionError(errMsg(e));
      setMergeOpen(false);
    } finally {
      setMerging(false);
    }
  };

  // ---- Permanent delete ----
  const doDelete = async () => {
    if (!deleteDoc || deleting) return;
    const target = deleteDoc;
    setDeleting(true);
    setActionError(null);
    setNotice(null);
    try {
      await api.delete(`/api/crm/documents/${target.id}`);
      setDocs((prev) => prev.filter((d) => d.id !== target.id));
      setDeleteDoc(null);
      if (editingId === target.id) cancelEdit();
      setNotice('Record deleted.');
    } catch (e: any) {
      setActionError(errMsg(e));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div
      className={frameless
        ? ''
        : 'rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-6'}
    >
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="inline-flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
          <FileText className="h-4 w-4 text-primary-600 dark:text-primary-400" />
          Medical records on file
        </h3>
        <div className="flex items-center gap-2">
          {selectMode ? (
            <button
              onClick={exitSelectMode}
              className="inline-flex min-h-[44px] items-center gap-1 rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
            >
              Cancel
            </button>
          ) : (
            <>
              {docs.length >= 2 && (
                <button
                  onClick={enterSelectMode}
                  title="Select records to combine into one"
                  className="inline-flex min-h-[44px] items-center gap-1 rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 transition-colors duration-150 hover:border-primary-400 hover:text-primary-700 dark:border-gray-600 dark:text-gray-200 dark:hover:border-primary-600 dark:hover:text-primary-300 sm:min-h-0"
                >
                  <Layers className="h-3.5 w-3.5" />
                  Combine
                </button>
              )}
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                title="Upload a PDF or photo of a medical record"
                className="inline-flex min-h-[44px] items-center gap-1 rounded-lg bg-primary-600 px-2.5 py-1.5 text-xs font-medium text-white transition-colors duration-150 hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0"
              >
                {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                {uploading ? 'Uploading…' : 'Upload record'}
              </button>
            </>
          )}
        </div>
        <input
          ref={fileRef}
          type="file"
          accept={ACCEPT}
          className="hidden"
          onChange={(e) => onFile(e.target.files?.[0] || null)}
        />
      </div>

      {selectMode && (
        <div className="mb-3 rounded-lg border border-primary-200 bg-primary-50 p-2.5 text-xs text-primary-800 dark:border-primary-800 dark:bg-primary-900/20 dark:text-primary-300">
          Select two or more records to combine them into one PDF (in the order listed).
        </div>
      )}

      {notice && (
        <div className="mb-3 rounded-lg border border-primary-200 bg-primary-50 p-2.5 text-xs text-primary-800 dark:border-primary-800 dark:bg-primary-900/20 dark:text-primary-300">
          {notice}
        </div>
      )}
      {(error || actionError) && (
        <div role="alert" className="mb-3 rounded-lg border border-red-200 bg-red-50 p-2.5 text-xs text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          {error || actionError}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
        </div>
      ) : docs.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No medical records yet. Faxed-in records and files you upload here appear together —
          upload a PDF or photo to have the AI review it overnight.
        </p>
      ) : (
        <ul className="space-y-2">
          {docs.map((d) => {
            const badge = sourceBadge(d.source);
            const meta = [fmtSize(d.file_size), relTime(d.created_at)].filter(Boolean).join(' · ');
            const checked = selectedIds.has(d.id);

            if (selectMode) {
              return (
                <li key={d.id}>
                  <button
                    onClick={() => toggleSelect(d.id)}
                    aria-pressed={checked}
                    className={`flex w-full items-center gap-3 rounded-lg border p-2.5 text-left transition-colors duration-150 ${
                      checked
                        ? 'border-primary-400 bg-primary-50/70 dark:border-primary-600 dark:bg-primary-900/20'
                        : 'border-gray-100 hover:border-primary-300 hover:bg-primary-50/40 dark:border-gray-700/60 dark:hover:border-primary-700'
                    }`}
                  >
                    {checked
                      ? <CheckSquare className="h-4 w-4 shrink-0 text-primary-600 dark:text-primary-400" />
                      : <Square className="h-4 w-4 shrink-0 text-gray-300 dark:text-gray-600" />}
                    <FileText className="h-4 w-4 shrink-0 text-gray-400" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-gray-900 dark:text-gray-100">{d.filename}</span>
                      <span className="mt-0.5 flex items-center gap-2">
                        <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium ${badge.cls}`}>
                          {badge.label}
                        </span>
                        {meta && <span className="truncate text-[11px] text-gray-400">{meta}</span>}
                      </span>
                    </span>
                  </button>
                </li>
              );
            }

            return (
              <li key={d.id}>
                {editingId === d.id ? (
                  <div className="flex items-center gap-2 rounded-lg border border-primary-300 p-2 dark:border-primary-700">
                    <FileText className="h-4 w-4 shrink-0 text-primary-600 dark:text-primary-400" />
                    <input
                      ref={editRef}
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); saveEdit(d); }
                        if (e.key === 'Escape') { e.preventDefault(); cancelEdit(); }
                      }}
                      maxLength={160}
                      placeholder="Name this record"
                      className="min-w-0 flex-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                    />
                    <button
                      onClick={() => saveEdit(d)}
                      disabled={savingId === d.id}
                      aria-label="Save name"
                      className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md text-green-600 hover:bg-green-50 disabled:opacity-50 dark:hover:bg-green-900/20 sm:min-h-0 sm:min-w-0 sm:p-1.5"
                    >
                      {savingId === d.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                    </button>
                    <button
                      onClick={cancelEdit}
                      disabled={savingId === d.id}
                      aria-label="Cancel rename"
                      className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-50 dark:hover:bg-gray-700 sm:min-h-0 sm:min-w-0 sm:p-1.5"
                    >
                      <X className="h-4 w-4" />
                    </button>
                    <span className="mx-0.5 h-5 w-px shrink-0 bg-gray-200 dark:bg-gray-600" aria-hidden="true" />
                    <button
                      onClick={() => setDeleteDoc(d)}
                      disabled={savingId === d.id}
                      aria-label="Delete record permanently"
                      title="Delete this record permanently"
                      className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md text-red-500 hover:bg-red-50 disabled:opacity-50 dark:hover:bg-red-900/20 sm:min-h-0 sm:min-w-0 sm:p-1.5"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div className="group flex items-center gap-1 rounded-lg border border-gray-100 pr-1 transition-colors duration-150 hover:border-primary-300 hover:bg-primary-50/50 dark:border-gray-700/60 dark:hover:border-primary-700 dark:hover:bg-primary-900/10">
                    <button
                      onClick={() => view(d.id)}
                      title="Open this record in a new tab"
                      className="flex min-w-0 flex-1 items-center gap-3 p-2.5 text-left"
                    >
                      <FileText className="h-4 w-4 shrink-0 text-gray-400 group-hover:text-primary-600 dark:group-hover:text-primary-400" />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-gray-900 dark:text-gray-100">{d.filename}</span>
                        <span className="mt-0.5 flex items-center gap-2">
                          <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium ${badge.cls}`}>
                            {badge.label}
                          </span>
                          {meta && <span className="truncate text-[11px] text-gray-400">{meta}</span>}
                        </span>
                      </span>
                      <ExternalLink className="h-3.5 w-3.5 shrink-0 text-gray-300 group-hover:text-primary-600 dark:group-hover:text-primary-400" />
                    </button>
                    {sentIds.has(d.id) ? (
                      <span
                        className="inline-flex shrink-0 items-center gap-1 px-2 text-[11px] font-medium text-green-600 dark:text-green-400"
                        title="Sent to CRIO"
                      >
                        <Check className="h-3.5 w-3.5" /> Sent
                      </span>
                    ) : (
                      <button
                        onClick={() => sendToCrio(d)}
                        disabled={sendingId === d.id}
                        aria-label="Send to CRIO"
                        title="Send this record to CRIO’s document inbox"
                        className="inline-flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-primary-600 disabled:opacity-50 dark:hover:bg-gray-700 dark:hover:text-primary-400 sm:min-h-0 sm:min-w-0 sm:p-1.5"
                      >
                        {sendingId === d.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                      </button>
                    )}
                    <button
                      onClick={() => startEdit(d)}
                      aria-label="Rename or delete record"
                      title="Rename or delete"
                      className="inline-flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-primary-600 dark:hover:bg-gray-700 dark:hover:text-primary-400 sm:min-h-0 sm:min-w-0 sm:p-1.5"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {selectMode && docs.length > 0 && (
        <div className="mt-3 flex items-center justify-between gap-2 border-t border-gray-100 pt-3 dark:border-gray-700">
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {selectedDocs.length === 0
              ? 'No records selected'
              : `${selectedDocs.length} selected${selectedSize ? ` · ${fmtSize(selectedSize)}` : ''}`}
          </span>
          <button
            onClick={() => setMergeOpen(true)}
            disabled={selectedDocs.length < 2}
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-medium text-white transition-colors duration-150 hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0"
          >
            <Layers className="h-3.5 w-3.5" />
            Combine{selectedDocs.length >= 2 ? ` ${selectedDocs.length}` : ''} records
          </button>
        </div>
      )}

      {mergeOpen && (
        <MergeRecordsModal
          count={selectedDocs.length}
          sizeLabel={fmtSize(selectedSize) || '—'}
          defaultName={`Combined records (${selectedDocs.length})`}
          isMerging={merging}
          onConfirm={doMerge}
          onCancel={() => { if (!merging) setMergeOpen(false); }}
        />
      )}

      {deleteDoc && (
        <DeleteRecordModal
          filename={deleteDoc.filename}
          isDeleting={deleting}
          onConfirm={doDelete}
          onCancel={() => { if (!deleting) setDeleteDoc(null); }}
        />
      )}
    </div>
  );
}
